import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import {
  BehaviorSubject,
  combineLatestWith,
  filter,
  forkJoin,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap
} from "rxjs";

import { DemandsService } from "../../authentication/services/demands.service";
import { OrganizationRepository } from "../../organizations/repositories/organization.repository";
import { Checkbox } from "../../shared/components/checkmarks/checkmarks.component";
import { SnackbarService } from '../../shared/components/snackbar/snackbar.service';
import { CreditCheckService } from '../../shared/services/credit-check.service';
import { FunnelService } from "../../shared/services/funnel.service";
import { capitalize } from "../../shared/tool-functions/capitalize";
import { Train } from "../../trains/models/train.entity";
import { TrainRepository } from "../../trains/repositories/train.repository";
import { Talker } from "../../users/models/talker.entity";
import { Role } from "../../users/models/users.entity";
import { ProfileService } from "../../users/services/profile.service";
import { mediumPillarColors, PillarSlug, PILLAR_TRANSLATIONS } from "../colors";
import { CreateTrainService } from "../create-train.service";
import { DynamicType } from "../models/dynamic.model";
import { Thematic } from "../models/thematic.model";
import { ThematicRepository } from "../repositories/thematic.repository";

@Component({
  selector: 'app-thematics-list',
  templateUrl: './thematics-list.component.html',
  styleUrls: [ './thematics-list.component.scss' ]
})
export class ThematicsListComponent implements OnInit, OnDestroy {

  private readonly update$ = new Subject<void>();

  allThematics: Thematic[] = [];

  thematicsShown: Thematic[] = [];

  thematicsShownPartOne: Thematic[] = [];

  remainingThematics: Thematic[] = [];

  archivedThematics: Thematic[] = [];

  available$ = new Subject<boolean>();

  availableTrains$: BehaviorSubject<Train[]> = new BehaviorSubject<Train[]>([]);

  destroy$ = new Subject<void>();

  quotaExceeded = false;

  noUnitAvailable = false;

  loading = true;

  pillarCheckboxes: Checkbox<PillarSlug>[] = [
    { id: 'body', key: PILLAR_TRANSLATIONS['body'], selected: false },
    { id: 'mind', key: PILLAR_TRANSLATIONS['mind'], selected: false },
    { id: 'relations', key: PILLAR_TRANSLATIONS['relations'], selected: false },
    { id: 'posture', key: PILLAR_TRANSLATIONS['posture'], selected: false },
  ];

  pillarCheckboxesSelected: Checkbox<PillarSlug>[] = [];

  onlyFavorites = false;

  favoriteThematics: Thematic[] = [];

  dynamicTypeCheckboxes: Checkbox<DynamicType>[] = [ {
    id: DynamicType.COACHING,
    key: "Coaching en groupe",
    selected: false,
  },
    {
      id: DynamicType.SPEECH,
      key: "Parole en groupe",
      selected: false,
    } ];

  dynamicTypeCheckboxesSelected: Checkbox<DynamicType>[] = [];

  alreadyAskForMoreQuota = false;

  alreadyAskForMoreUnits = false;

  isPilot = false;

  thematicsOrdered: Thematic[] = [];

  leavingFunnel = false;

  enteringFunnel = false;

  public readonly cardTrackBy = (_: number, card: Thematic) => card.id;

  private readonly specialSubscriptionPlans: string[] = [
    'tester-subscription',
    'personalized-subscription-individual',
    'personalized-subscription'
  ];

  isSpecialPlan = false;

  constructor(private readonly thematicRepository: ThematicRepository,
              private readonly router: Router,
              private readonly profileService: ProfileService,
              private readonly organizationRepository: OrganizationRepository,
              private readonly demandService: DemandsService,
              private readonly createTrainService: CreateTrainService,
              public readonly funnelService: FunnelService,
              private readonly snackbarService: SnackbarService,
              private readonly creditCheckService: CreditCheckService,
              private readonly trainRepository: TrainRepository,
              private route: ActivatedRoute) {
  }


  ngOnInit(): void {
    this.thematicRepository.getThematicsOrderByTrains()
      .pipe(
        combineLatestWith(this.thematicRepository.getAvailableOnes()),
        takeUntil(this.destroy$))
      .subscribe(([ thematicsOrdered, allThematics ]) => {
        this.thematicsOrdered = thematicsOrdered.slice(0, 6);
        this.allThematics = allThematics;
        this.filter();
      });
    
    this.route.queryParams.subscribe(params => {
      const typeFromParams = params.type;
      if (typeFromParams) {
        this.selectDynamicFromParams(typeFromParams);
      }
    });

    // this.funnelService.leavingFunnel$
    //   .pipe(
    //     tap(() => {
    //       this.leavingFunnel = true;
    //     }),
    //     delay(this.funnelService.ANIMATION_DELAY),
    //     tap(() => {
    //       this.leavingFunnel = false;
    //     }),
    //     takeUntil(this.destroy$))
    //   .subscribe();

    this.createTrainService.clear();

    this.available$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loading = false;
      });


    this.update$
      .pipe(
        startWith(() => {
        }),
        switchMap(() => this.thematicRepository.getAvailableOnes()),
        tap(thematics => {
          this.allThematics = thematics;
        }),
        takeUntil(this.destroy$),
        switchMap(() => this.profileService.getMyProfile()),
        tap(profile => {
          if (profile?.role !== Role.Talker) {
            this.available$.next(false);
          } else {
            if (profile.isPilot) {
              this.isPilot = true;
            }
            this.favoriteThematics = profile.favoriteThematics ?? [];
          }
        }),
        filter((profile): profile is Talker => profile?.role === Role.Talker),
        switchMap(() => forkJoin([ this.trainRepository.getAvailableTrains(), this.organizationRepository.canConsumeUnit() ]))
      )
      .subscribe(
        ([ availableTrains, canConsumeResult ]) => {
          this.availableTrains$.next(availableTrains);
          this.isSpecialPlan = this.specialSubscriptionPlans.includes((this.profileService.profile as Talker)?.organization.subscriptionPlan?.slug ?? "");
          this.quotaExceeded = !canConsumeResult.quota;
          const minimumJourneyCredits = Math.min(
            canConsumeResult.creditCosts?.speechTrain ?? 10,
            canConsumeResult.creditCosts?.coachingTrain ?? 10,
          );
          this.noUnitAvailable = canConsumeResult.units < minimumJourneyCredits;
          this.alreadyAskForMoreQuota = canConsumeResult.alreadyAskForMoreQuota;
          this.alreadyAskForMoreUnits = canConsumeResult.alreadyAskForMoreUnits;

          this.available$.next(true);
        }
      );
  }

  askForMoreQuota(): void {
    this.demandService.askForMoreQuota((this.profileService.profile as Talker)?.organization?.id ?? "").subscribe(() => {
      this.alreadyAskForMoreQuota = true;
    });
  }

  askForMoreUnits(): void {
    if (this.isSpecialPlan) {
      this.creditCheckService.askForMoreUnits(this.selectedUpsellJourneyType).subscribe();
      return;
    }
    this.demandService.askForMoreUnits((this.profileService.profile as Talker)?.organization?.id ?? "").subscribe(() => {
      this.snackbarService.pushMessage("Votre demande de crédits a bien été envoyée", 'success');
      this.alreadyAskForMoreUnits = true;
    });
  }

  goToCompanyOffers(): void {
    this.router.navigateByUrl('/offres-entreprises-2026');
  }

  goToQuestions(): void {
    this.router.navigateByUrl('/questions');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  clickOnThematic(thematic: Thematic, isPopular = false) {
    (window._mtm || []).push({ 'event': 'clic-thematique', 'nom-parcours': thematic.title });
    this.router.navigate([ 'thematics', thematic.slug ]);
  }

  addThematic() {
    this.router.navigate([ 'thematics', 'new', 'edit' ]);
  }

  selectPillar(slug: PillarSlug) {
    const pillar = this.pillarCheckboxes.find(p => p.id === slug);
    if (pillar) {
      pillar.selected = !pillar.selected;
    }
    this.pillarCheckboxesSelected = this.pillarCheckboxes.filter(checkbox => checkbox.selected);
    this.filter();
  }

  selectDynamicFromParams(type: string) {
    const dynamic = this.dynamicTypeCheckboxes.find(c => c.id === type);
    if (dynamic) {
      this.dynamicTypeCheckboxes.forEach(c => c.selected = false);
      dynamic.selected = true;
      this.dynamicTypeCheckboxesSelected = [dynamic];
      this.filter();
    }
  }

  selectDynamic(type: string) {
    const dynamic = this.dynamicTypeCheckboxes.find(c => c.id === type);
    if (dynamic) {
      dynamic.selected = !dynamic.selected;
    }
    this.dynamicTypeCheckboxesSelected = this.dynamicTypeCheckboxes.filter(checkbox => checkbox.selected);
    this.filter();
  }

  get selectedUpsellJourneyType(): DynamicType | undefined {
    return this.dynamicTypeCheckboxesSelected.length === 1
      ? this.dynamicTypeCheckboxesSelected[0]?.id
      : undefined;
  }

  selectFavorite() {
    this.onlyFavorites = !this.onlyFavorites;
    this.filter();
  }

  filter() {
    const selectedPillars = this.pillarCheckboxes.filter(p => p.selected).map(p => p.id);
    const selectedDynamics = this.dynamicTypeCheckboxes.filter(d => d.selected);

    // Apply all filters
    const filteredThematics = this.allThematics
      // Filter by pillar (if any selected)
      .filter(thematic =>
        selectedPillars.length === 0 || selectedPillars.includes(thematic.pillar?.name as PillarSlug)
      )
      // Filter by dynamic type (if any selected)
      .filter(thematic =>
        selectedDynamics.length === 0 ||
        selectedDynamics.length === 2 ||
        thematic.dynamics.map(d => d.type).includes(selectedDynamics[0].id)
      )
      // Filter by favorites (if enabled)
      .filter(thematic =>
        !this.onlyFavorites || this.favoriteThematics.map(ft => ft.id).includes(thematic.id)
      );

    // Separate non-archived and archived thematics
    const nonArchived = filteredThematics.filter(t => !t.isArchived);

    // Upper part: first 18 non-archived thematics
    this.thematicsShownPartOne = nonArchived.slice(0, 18);

    // Middle part: remaining non-archived (after 18th)
    this.remainingThematics = nonArchived.slice(18);

    // Archived thematics in separate section
    this.archivedThematics = filteredThematics.filter(t => t.isArchived);

    // Keep thematicsShown for reference
    this.thematicsShown = filteredThematics;
  }

  protected readonly mediumPillarColors = mediumPillarColors;

  protected readonly capitalize = capitalize;

  protected readonly DynamicType = DynamicType;

  getThematicProblem(thematic: Thematic): string | undefined {
    if (thematic.isArchived) {
      return 'Thématique archivée';
    }
    if (thematic.dynamics?.length === 0) {
      return 'Thématique sans dynamiques';
    }
    const speechDynamic = thematic.dynamics?.find(dynamic => dynamic.type === DynamicType.SPEECH);
    if (speechDynamic?.isArchived === false && speechDynamic?.sherpasAllowed?.length === 0) {
      return 'Parole en groupe sans sherpa';
    }

    const coachingDynamic = thematic.dynamics?.find(dynamic => dynamic.type === DynamicType.COACHING);
    if (coachingDynamic?.isArchived === false && coachingDynamic?.sherpasAllowed?.length === 0) {
      return 'Parole en groupe sans sherpa';
    }

    return undefined;
  }

  goToOrientation(){
    this.router.navigate(["home", "orientation"]);
  }
}
